import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';

// ─── Import the MP3 so Webpack/CRA bundles it and gives us the real URL ────────
// File must be at:  src/sprites/sounds/spongebob-bubble-transition.mp3
let BUBBLE_SOUND_SRC = null;
try {
  // eslint-disable-next-line import/no-webpack-loader-syntax
  BUBBLE_SOUND_SRC = require('../sprites/sounds/spongebob-bubble-transition.mp3');
  // CRA returns the default export as the URL string
  if (typeof BUBBLE_SOUND_SRC === 'object') BUBBLE_SOUND_SRC = BUBBLE_SOUND_SRC.default;
} catch (e) {
  console.warn('[BubbleTransition] Sound file not found – check src/sprites/sounds/');
}

const BubbleTransitionContext = createContext(null);

export function useBubbleTransition() {
  return useContext(BubbleTransitionContext);
}

// ─── Pre-build the bubble grid (stable across renders) ────────────────────────
// Each bubble stores its cover-delay (left→right) and reveal-delay (right→left).
function buildBubbles() {
  const COLS = 9;
  const ROWS = 6;
  const COL_STEP = 0.058; // s
  const ROW_STEP = 0.018; // s
  const maxD = (COLS - 1) * COL_STEP + (ROWS - 1) * ROW_STEP; // ≈ 0.554s

  return Array.from({ length: COLS * ROWS }, (_, i) => {
    const c = i % COLS;
    const r = Math.floor(i / COLS);
    const jitter = Math.random() * 0.012;
    const coverDelay  = c * COL_STEP + r * ROW_STEP + jitter;
    const revealDelay = maxD - coverDelay + Math.random() * 0.008;
    return {
      id: i,
      left: (c / COLS) * 100 + (Math.random() * 5 - 2.5),
      top:  (r / ROWS) * 100 + (Math.random() * 5 - 2.5),
      size: 16 + Math.random() * 7, // vmax – big enough to overlap neighbours
      coverDelay,
      revealDelay,
    };
  });
}

// ─── Timing (ms) ──────────────────────────────────────────────────────────────
// Cover: each bubble's expand animation = 500ms, max stagger ≈ 554ms → total ≈ 1100ms
// Reveal: same durations in reverse
const COVER_TOTAL  = 1150; // ms to wait until every bubble is fully expanded
const REVEAL_PAUSE = 100;  // ms to hold at "fully covered" before revealing
const REVEAL_TOTAL = 1150; // ms until every bubble has fully collapsed

// ─── Provider ─────────────────────────────────────────────────────────────────
export function BubbleTransitionProvider({ children }) {
  const [phase, setPhase]   = useState('idle'); // idle | covering | covered | revealing
  const bubbles              = useRef(buildBubbles());
  const running              = useRef(false);
  const timers               = useRef([]);

  const after = (ms, fn) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
  };

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // ── Play the imported sound ──────────────────────────────────────────────────
  const playSound = () => {
    if (!BUBBLE_SOUND_SRC) return;
    try {
      const a = new Audio(BUBBLE_SOUND_SRC);
      a.volume = 0.75;
      a.play().catch(() => {});
    } catch (_) {}
  };

  // ── Main trigger ─────────────────────────────────────────────────────────────
  // Pass a callback that will be executed once the screen is fully covered.
  // Because we use react-router's `navigate()` as the callback, the React tree
  // stays mounted and the reveal plays on the new route automatically.
  const trigger = useCallback((callback) => {
    if (running.current) return;
    running.current = true;
    timers.current.forEach(clearTimeout);
    timers.current = [];

    playSound();
    setPhase('covering');

    after(COVER_TOTAL, () => {
      setPhase('covered');                        // screen is fully covered
      after(REVEAL_PAUSE, () => {
        if (callback) callback();                 // navigate / scroll NOW
        after(20, () => {                         // tiny gap for React re-render
          setPhase('revealing');
          after(REVEAL_TOTAL, () => {
            setPhase('idle');
            running.current = false;
          });
        });
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BubbleTransitionContext.Provider value={trigger}>
      {children}

      {phase !== 'idle' && (
        <div className={`bubble-wipe-overlay bubble-wipe-${phase}`} aria-hidden="true">
          {bubbles.current.map((b) => (
            <div
              key={b.id}
              className="bubble-wipe-cell"
              style={{
                left: `${b.left}%`,
                top:  `${b.top}%`,
                '--cover-delay':  `${b.coverDelay.toFixed(3)}s`,
                '--reveal-delay': `${b.revealDelay.toFixed(3)}s`,
                '--size': `${b.size}vmax`,
              }}
            />
          ))}
        </div>
      )}
    </BubbleTransitionContext.Provider>
  );
}